
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NavBar from '@/components/NavBar';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Sun, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import Footer from '@/components/Footer';

const WaitlistPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const { error } = await supabase
        .from('waitlist')
        .insert([{ email }]);

      if (error) {
        if (error.code === '23505') { // Unique constraint violation
          toast({
            title: "Already on the waitlist!",
            description: "This email is already registered. We'll notify you when we launch!",
            variant: "default"
          });
        } else {
          throw error;
        }
      } else {
        setIsSubmitted(true);
        toast({
          title: "Welcome to the waitlist!",
          description: "We'll notify you as soon as FollowTheSun launches!",
        });
      }
    } catch (error) {
      console.error('Waitlist signup error:', error);
      toast({
        title: "Something went wrong",
        description: "Please try again later or contact us directly.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="flex flex-col min-h-screen bg-warm-cream">
        <NavBar />
        
        <main className="flex-1 flex items-center justify-center py-12">
          <Card className="w-full max-w-md border-peach-orange/20">
            <CardHeader className="space-y-1 text-center">
              <div className="flex justify-center mb-4">
                <CheckCircle className="h-16 w-16 text-peach-orange" />
              </div>
              <CardTitle className="text-2xl font-bold text-center">You're on the list!</CardTitle>
              <CardDescription className="text-center">
                Thanks for joining our waitlist. We'll email you as soon as FollowTheSun launches!
              </CardDescription>
            </CardHeader>
            <CardFooter className="flex flex-col space-y-4">
              <Button 
                onClick={() => navigate('/')} 
                className="w-full"
                variant="default"
              >
                Back to Home
              </Button>
            </CardFooter>
          </Card>
        </main>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-warm-cream">
      <NavBar />
      
      <main className="flex-1 flex items-center justify-center py-12">
        <Card className="w-full max-w-md border-peach-orange/20">
          <CardHeader className="space-y-1">
            <div className="flex justify-center mb-2">
              <Sun className="h-10 w-10 text-peach-orange animate-sunshine" />
            </div>
            <CardTitle className="text-2xl font-bold text-center">Join the Waitlist</CardTitle>
            <CardDescription className="text-center">
              Be the first to know when FollowTheSun launches and discover the best sunny terraces in Stockholm!
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input 
                  id="email" 
                  type="email" 
                  placeholder="hello@example.com" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="border-peach-orange/30"
                />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? "Joining waitlist..." : "Join Waitlist"}
              </Button>
            </form>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <div className="text-center text-sm text-muted-foreground">
              Already have an account?{" "}
              <Button variant="link" className="p-0 h-auto" onClick={() => navigate('/login')}>
                Sign in
              </Button>
            </div>
          </CardFooter>
        </Card>
      </main>
      
      <Footer />
    </div>
  );
};

export default WaitlistPage;
